import { io } from 'socket.io-client';

// Try to get server URL from environment or localStorage, or use default
const envSocketURL = process.env.REACT_APP_API_URL;
const savedSocketURL = localStorage.getItem('server_url');
const SERVER_URL = envSocketURL || savedSocketURL || 'http://localhost:4001';

console.log('Initializing shared socket with URL:', SERVER_URL);

// Singleton socket instance with better connection options
const socket = io(SERVER_URL, {
  transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
  reconnection: true,
  reconnectionAttempts: 8,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  autoConnect: true,
  forceNew: false,
  withCredentials: true,
  // Set up proper timeout for ACK packets
  ackTimeout: 10000,
});

// ============================================================================
// GAME CONTEXT MANAGEMENT (for automatic reconnection)
// ============================================================================

// Store current game context for reconnection after network changes
let currentGameContext = null;

/**
 * Set the current game context to enable automatic room rejoin on reconnect
 * @param {string} gameId - The game ID
 * @param {string} playerName - The player's name
 * @param {string} screen - Current screen ('lobby' or 'game')
 */
export const setGameContext = (gameId, playerName, screen) => {
  currentGameContext = { gameId, playerName, screen, timestamp: Date.now() };

  // Persist to localStorage for recovery after page refresh
  try {
    localStorage.setItem('qwixx_game_context', JSON.stringify(currentGameContext));
    console.log('✅ Game context saved:', currentGameContext);
  } catch (error) {
    console.error('Failed to save game context:', error);
  }
};

/**
 * Clear the game context (call when leaving game/lobby)
 */
export const clearGameContext = () => {
  currentGameContext = null;

  try {
    localStorage.removeItem('qwixx_game_context');
    console.log('✅ Game context cleared');
  } catch (error) {
    console.error('Failed to clear game context:', error);
  }
};

/**
 * Get the current game context
 * @returns {Object|null} The game context or null
 */
export const getGameContext = () => {
  if (currentGameContext) {
    return currentGameContext;
  }

  // Try to restore from localStorage
  try {
    const stored = localStorage.getItem('qwixx_game_context');
    if (stored) {
      const parsed = JSON.parse(stored);

      // Check if context is not too old (max 1 hour)
      const age = Date.now() - (parsed.timestamp || 0);
      if (age < 3600000) { // 1 hour
        currentGameContext = parsed;
        return currentGameContext;
      } else {
        // Context too old, clear it
        clearGameContext();
      }
    }
  } catch (error) {
    console.error('Failed to restore game context:', error);
  }

  return null;
};

// ============================================================================
// ENHANCED SOCKET EVENT EMITTER
// ============================================================================

const safeEmit = (eventName, data, callback) => {
  if (!socket.connected) {
    console.warn(`Cannot emit ${eventName} - socket not connected`);
    if (callback) {
      callback(new Error('Socket not connected'), null);
    }
    return;
  }

  try {
    socket.timeout(10000).emit(eventName, data, (err, response) => {
      if (err) {
        console.error(`Error in ${eventName} event:`, err);
        if (callback) callback(err, null);
        return;
      }

      // Modified to accept undefined/null responses
      // Some events may not return a response object
      if (callback) callback(null, response || { success: true });
    });
  } catch (error) {
    console.error(`Exception emitting ${eventName}:`, error);
    if (callback) callback(error, null);
  }
};

// Add the safeEmit method to the socket object
socket.safeEmit = safeEmit;

// ============================================================================
// AUTOMATIC RECONNECTION LOGIC
// ============================================================================

// Log socket connection events
socket.on('connect', () => {
  console.log('✅ Shared socket connected! ID:', socket.id);

  // Try to restore game context and automatically rejoin room
  const context = getGameContext();

  if (context && context.gameId && context.playerName) {
    const { gameId, playerName, screen } = context;

    console.log(`🔄 Auto-rejoining ${screen} for game ${gameId} as ${playerName}`);

    // Small delay to ensure server is ready
    setTimeout(() => {
      if (screen === 'lobby') {
        // Rejoin lobby session
        socket.emit('joinGameSession', { gameId, playerName }, (response) => {
          if (response?.error) {
            console.error('❌ Failed to auto-rejoin lobby:', response.error);
            // Don't clear context - user might manually retry
          } else {
            console.log('✅ Successfully auto-rejoined lobby');
          }
        });
      } else if (screen === 'game') {
        // Rejoin game room
        socket.emit('joinGameRoom', { gameId, playerName }, (response) => {
          if (response?.error) {
            console.error('❌ Failed to auto-rejoin game:', response.error);
            // Don't clear context - user might manually retry
          } else {
            console.log('✅ Successfully auto-rejoined game');
          }
        });
      }
    }, 500);
  }
});

socket.on('reconnecting', (attemptNumber) => {
  console.log(`🔄 Reconnecting... Attempt ${attemptNumber}/8`);
});

socket.on('reconnect', (attemptNumber) => {
  console.log(`✅ Reconnected after ${attemptNumber} attempts`);
});

socket.on('reconnect_failed', () => {
  console.error('❌ Failed to reconnect after 8 attempts');
});

socket.on('connect_error', (err) => {
  console.error('❌ Shared socket connection error:', err.message);
});

socket.on('disconnect', (reason) => {
  console.log(`🔌 Shared socket disconnected! Reason: ${reason}`);

  // Log additional context for debugging
  const context = getGameContext();
  if (context) {
    console.log(`📍 Disconnected while in ${context.screen} for game ${context.gameId}`);
  }
});

socket.on('error', (err) => {
  console.error('⚠️ Shared socket error:', err);
});

// Socket middleware for debugging
socket.onAny((event, ...args) => {
  if (!event.startsWith('_')) { // Don't log internal events
    console.log(`📣 [Socket Event: ${event}]`, args);
  }
});

// ============================================================================
// VISIBILITY CHANGE HANDLER (for app backgrounding/foregrounding)
// ============================================================================

// Handle app returning from background (e.g., after WhatsApp share)
// Use multiple event listeners as fallbacks for different browsers/PWAs
if (typeof document !== 'undefined' || typeof window !== 'undefined') {

  // Function to rejoin room when app comes back to foreground
  const handleAppForeground = (eventSource) => {
    console.log(`🔄 App returned to foreground (${eventSource}), checking socket...`);

    const context = getGameContext();

    // If we have an active game, ensure we rejoin the socket room
    if (context && context.gameId && context.playerName) {
      const { gameId, playerName, screen } = context;
      console.log(`🔄 Active game detected (${gameId}), ensuring room membership...`);

      // First ensure socket is connected
      if (!socket.connected) {
        console.log('🔌 Socket disconnected, reconnecting...');
        socket.connect();
      }

      // Wait for socket to be ready, then rejoin room directly
      const rejoinRoom = (attempt = 0) => {
        if (!socket.connected) {
          if (attempt < 30) { // Try for up to 3 seconds
            console.log(`⏳ Waiting for socket connection... (attempt ${attempt + 1})`);
            setTimeout(() => rejoinRoom(attempt + 1), 100);
          } else {
            console.error('❌ Socket connection timeout, giving up');
          }
          return;
        }

        console.log(`🔄 Rejoining ${screen} room for game ${gameId}...`);
        if (screen === 'lobby') {
          socket.emit('joinGameSession', { gameId, playerName }, (response) => {
            if (response?.error) {
              console.error('❌ Failed to rejoin lobby:', response.error);
            } else {
              console.log('✅ Successfully rejoined lobby room');
            }
          });
        } else if (screen === 'game') {
          socket.emit('joinGameRoom', { gameId, playerName }, (response) => {
            if (response?.error) {
              console.error('❌ Failed to rejoin game:', response.error);
            } else {
              console.log('✅ Successfully rejoined game room');
            }
          });
        }
      };

      // Small delay to let any pending socket operations complete
      setTimeout(() => rejoinRoom(0), 200);
    } else if (!socket.connected) {
      console.log('🔌 Socket disconnected while in background, reconnecting...');
      socket.connect();
    }
  };

  // Primary: visibilitychange (works on most browsers)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      handleAppForeground('visibilitychange');
    } else {
      console.log('📱 App went to background (visibilitychange)');
    }
  });

  // Fallback 1: pageshow (works better on iOS Safari and some PWAs)
  window.addEventListener('pageshow', (event) => {
    // event.persisted is true if page was restored from bfcache
    console.log(`📱 pageshow event fired (persisted: ${event.persisted})`);
    handleAppForeground('pageshow');
  });

  // Fallback 2: focus (works on some mobile browsers when switching apps)
  window.addEventListener('focus', () => {
    handleAppForeground('focus');
  });

  // Log when app goes to background
  window.addEventListener('pagehide', () => {
    console.log('📱 App went to background (pagehide)');
  });

  window.addEventListener('blur', () => {
    console.log('📱 App lost focus (blur)');
  });

  // Fallback 3: resume event (Page Lifecycle API - useful for Android PWAs)
  document.addEventListener('resume', () => {
    handleAppForeground('resume');
  });

  // Fallback 4: online event (fires when network reconnects)
  window.addEventListener('online', () => {
    handleAppForeground('online');
  });

  // ============================================================================
  // ANDROID-SPECIFIC POLLING FALLBACK
  // ============================================================================
  // Android Chrome/WebView PWAs don't reliably fire visibility events when
  // returning from background. This polling fallback ensures we stay in sync.
  const isAndroid = /Android/i.test(navigator.userAgent);
  if (isAndroid) {
    console.log('📱 Android detected - enabling polling fallback for room membership');

    setInterval(() => {
      // Only act when app is visible and socket is connected
      if (document.visibilityState === 'visible' && socket.connected) {
        const context = getGameContext();
        if (context && context.gameId && context.playerName) {
          const { gameId, playerName, screen } = context;

          // Silently ensure we're in the room (server handles duplicate joins gracefully)
          if (screen === 'lobby') {
            socket.emit('joinGameSession', { gameId, playerName }, (response) => {
              if (response?.error) {
                console.log('📍 Android poll: lobby room check failed:', response.error);
              }
            });
          } else if (screen === 'game') {
            socket.emit('joinGameRoom', { gameId, playerName }, (response) => {
              if (response?.error) {
                console.log('📍 Android poll: game room check failed:', response.error);
              }
            });
          }
        }
      }
    }, 5000); // Check every 5 seconds
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default socket; 